import { useCallback, useEffect, useRef, useState } from "react";
import { trace } from "../utils/debugTrace";

/** 取得に失敗している間の再試行間隔 */
const RETRY_INTERVAL_MS = 5000;

const TRACE_CHANNEL = "wake-lock";

/**
 * 画面スリープ防止（Screen Wake Lock API）。
 *
 * PRODUCT_DEV-586: 録音中にスマホがスリープすると MediaRecorder / MediaStream が
 * OS に破棄され録音が飛ぶ。それを防ぐため、録音フック（useAudioRecorder /
 * useChunkedAudioRecorder / useRealtimeConnection）がこのフックを内包して呼ぶ。
 * 呼び出しを画面ではなく録音フックに置くことで、新しい録音画面を作っても
 * スリープ防止の付け忘れが起きない。
 *
 * `enabled` は「録音中か」であり、一時停止中も true のままにすること。
 * pause 中にスリープさせると録音そのものが失われる。
 *
 * OS/ブラウザは wake lock を随時解放してくるため、単に一度 request するだけでは
 * 不十分:
 * - 非表示になると自動解放される → 復帰時に再取得する
 * - 表示中でも解放されることがある → release イベントで再取得する
 * - 低電力モード等で request が拒否される → 一定間隔で再試行する
 *
 * 未対応ブラウザでは何もしない（no-op）。
 */
export function useScreenWakeLock(enabled: boolean): { isActive: boolean } {
  const [isActive, setIsActive] = useState(false);
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  // enabled=false / アンマウント後に in-flight の request が解決しても取得しないためのガード
  const enabledRef = useRef(enabled);
  // acquire の多重実行防止
  const acquiringRef = useRef(false);

  const releaseCb = useCallback(async () => {
    const sentinel = sentinelRef.current;
    sentinelRef.current = null;
    setIsActive(false);
    if (!sentinel) return;
    try {
      await sentinel.release();
    } catch (error) {
      trace(TRACE_CHANNEL, "release-failed", { error: String(error) });
    }
  }, []);

  useEffect(() => {
    enabledRef.current = enabled;

    const nav = navigator as Navigator & {
      wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinel> };
    };
    const wakeLock = nav.wakeLock;
    if (!enabled || !wakeLock?.request) {
      // 未対応ブラウザは no-op。enabled=false なら下の cleanup 経由で解放済み。
      if (enabled) trace(TRACE_CHANNEL, "unsupported");
      return;
    }

    async function acquire(reason: string) {
      if (!enabledRef.current) return;
      if (sentinelRef.current || acquiringRef.current) return;
      if (document.visibilityState !== "visible") return;

      acquiringRef.current = true;
      try {
        const sentinel = await wakeLock.request("screen");
        // request 中に録音が終わった / アンマウントされた場合は即解放する
        if (!enabledRef.current) {
          await sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
        setIsActive(true);
        trace(TRACE_CHANNEL, "acquired", { reason });

        sentinel.addEventListener("release", () => {
          // OS/ブラウザ都合の自動解放。録音中で表示中なら取り直す。
          if (sentinelRef.current !== sentinel) return;
          sentinelRef.current = null;
          setIsActive(false);
          trace(TRACE_CHANNEL, "auto-released", {
            visibility: document.visibilityState,
          });
          void acquire("re-acquire-after-auto-release");
        });
      } catch (error) {
        // NotAllowedError（低電力モード、非表示、権限なし 等）。
        // 諦めず retry インターバルで再試行する。
        trace(TRACE_CHANNEL, "acquire-failed", {
          reason,
          error: String(error),
        });
      } finally {
        acquiringRef.current = false;
      }
    }

    void acquire("enabled");

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void acquire("visible");
      } else {
        // 非表示中は OS が自動解放するため、こちらでも state を落としておく
        void releaseCb();
      }
    };
    // bfcache 復帰では visibilitychange が発火しない環境があるため pageshow も見る
    const onPageShow = () => void acquire("pageshow");

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);

    // 取得できていない間だけ再試行する（取得済みなら acquire は即 return する）
    const retryTimer = setInterval(() => void acquire("retry"), RETRY_INTERVAL_MS);

    return () => {
      enabledRef.current = false;
      clearInterval(retryTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
      void releaseCb();
    };
  }, [enabled, releaseCb]);

  return { isActive };
}
